import {
    BaseCompElement,
    CORNERS,
    Dimension,
    type ElementMoveParams,
    getBoundingBoxAfterRotation,
    GroupElement,
    HandlerInteractionStates,
    InteractionStates,
    type MultipleElementMoveParams,
    Position,
    ROTATIONS,
    Scrollbars,
    SIDES,
    TextElement,
    type ValidationManagerForMultiPage,
} from '@bynder-studio/render-core';
import { getWhich, isArrowKey, isCtrlKey, isOptionAltKey, isShiftKey } from '../../../Helpers/hotKeys';
import TransformerBox from './TransformerBox';
import BaseManipulation from '../BaseManipulation';
import HoverBox from './HoverBox';
import BoundingBoxManipulationCompositor from './BoundingBoxManipulationCompositor';

const SCROLLBARS = Object.values(Scrollbars);

const getRotatedCursor = (angle = 0) =>
    `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='26' height='26' viewBox='0 0 24 24' fill='none' transform='rotate(${angle}deg)' style='&%2310; transform: rotate(${angle}deg);&%2310;'%3E%3Cg filter='url(%23filter0_dd_1407_21304)'%3E%3Cpath d='M18.6842 2.28198L22 5.59777L19.6316 5.59777C19.6316 7.2151 19.313 8.8166 18.6941 10.3108C18.0752 11.805 17.168 13.1627 16.0244 14.3064C14.8807 15.45 13.5231 16.3572 12.0288 16.9761C10.5346 17.595 8.93312 17.9136 7.31579 17.9136L7.31579 20.282L4 16.9761L7.31579 13.6504L7.31579 16.0892C8.69355 16.0892 10.0578 15.8179 11.3307 15.2906C12.6036 14.7634 13.7602 13.9906 14.7344 13.0164C15.7086 12.0421 16.4814 10.8856 17.0086 9.61268C17.5359 8.3398 17.8073 6.97553 17.8073 5.59777L15.3684 5.59777L18.6842 2.28198Z' fill='black'/%3E%3Cpath d='M18.9494 2.01682L18.6842 1.75165L18.419 2.01682L15.1033 5.33261L14.4631 5.97277L15.3684 5.97277L17.4253 5.97277C17.3808 7.17332 17.1227 8.3574 16.6622 9.46918C16.1538 10.6966 15.4086 11.8118 14.4692 12.7512C13.5298 13.6906 12.4146 14.4358 11.1872 14.9442L11.3307 15.2906L11.1872 14.9442C10.0754 15.4047 8.89134 15.6628 7.69079 15.7073L7.69079 13.6504L7.69079 12.7432L7.05023 13.3856L3.73444 16.7113L3.46967 16.9769L3.73523 17.2416L7.05102 20.5475L7.69079 21.1854L7.69079 20.282L7.69079 18.283C9.22951 18.2375 10.7482 17.9124 12.1723 17.3225L12.0288 16.9761L12.1723 17.3225C13.7121 16.6848 15.1111 15.75 16.2895 14.5715C17.468 13.3931 18.4028 11.994 19.0405 10.4543C19.6304 9.03019 19.9556 7.51149 20.001 5.97277L22 5.97277L22.9053 5.97277L22.2652 5.33261L18.9494 2.01682Z' stroke='white' stroke-width='0.75'/%3E%3C/g%3E%3Cdefs%3E%3Cfilter id='filter0_dd_1407_21304' x='0.939453' y='0.221436' width='24.8711' height='24.8674' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'%3E%3CfeFlood flood-opacity='0' result='BackgroundImageFix'/%3E%3CfeColorMatrix in='SourceAlpha' type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0' result='hardAlpha'/%3E%3CfeOffset dy='1'/%3E%3CfeGaussianBlur stdDeviation='1'/%3E%3CfeComposite in2='hardAlpha' operator='out'/%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0'/%3E%3CfeBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow_1407_21304'/%3E%3CfeColorMatrix in='SourceAlpha' type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0' result='hardAlpha'/%3E%3CfeOffset dy='0.5'/%3E%3CfeGaussianBlur stdDeviation='0.5'/%3E%3CfeComposite in2='hardAlpha' operator='out'/%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0'/%3E%3CfeBlend mode='normal' in2='effect1_dropShadow_1407_21304' result='effect2_dropShadow_1407_21304'/%3E%3CfeBlend mode='normal' in='SourceGraphic' in2='effect2_dropShadow_1407_21304' result='shape'/%3E%3C/filter%3E%3C/defs%3E%3C/svg%3E") 13 13, pointer`;

class BoundingBoxManipulation extends BaseManipulation {
    transformerBox: TransformerBox = null;

    private drawTransformerBoxRequestId: number = null;

    private drawHoverBoxRequestId: number = null;

    private keepAspectRatio = false;

    private delayElementSelection = false;

    private unselectElementId = null;

    private pressedElId: number = null;

    private hoverBox: HoverBox = null;

    hoverElId: number = null;

    private boundingBoxCompositor: BoundingBoxManipulationCompositor;

    private initialDegree = 0;

    private lastElementDegree = 0;

    private validationManager: ValidationManagerForMultiPage;

    redraw: (handleIndex: CORNERS | SIDES | null, state: HandlerInteractionStates | null) => void = null;

    constructor(compositor, containerDocument, emitter, redraw, validationManager: ValidationManagerForMultiPage) {
        super(compositor, containerDocument, emitter);
        this.redraw = redraw;
        this.validationManager = validationManager;
        this.boundingBoxCompositor = new BoundingBoxManipulationCompositor(
            this.compositor,
            this.canvas.getContext('2d'),
            this.compositor.devicePixelRatio,
        );
        this.canvas.addEventListener('mousedown', this.onMouseDown);
        this.containerDocument.addEventListener('mouseup', this.onMouseUp);
        this.containerDocument.addEventListener('mousemove', this.onMouseMove);
    }

    static movePositionByArrows(e, oldPosition): Position {
        const pxToAdd = isShiftKey(e) ? 10 : 1;
        let yToAdd = 0;
        let xToAdd = 0;

        switch (e.key) {
            case 'ArrowLeft': {
                xToAdd = pxToAdd * -1;
                break;
            }
            case 'ArrowRight': {
                xToAdd = pxToAdd;
                break;
            }
            case 'ArrowUp': {
                yToAdd = pxToAdd * -1;
                break;
            }
            case 'ArrowDown': {
                yToAdd = pxToAdd;
                break;
            }

            default:
                return oldPosition;
        }

        return oldPosition.add(new Position(xToAdd, yToAdd));
    }

    selectElement({
        topLevelSelectedQuantity,
        selectedElements,
        currentSelectedGroupId,
        currentParentId,
    }: {
        selectedElements: number[] | [];
        topLevelSelectedQuantity?: number;
        currentSelectedGroupId?: number | null;
        currentParentId?: number | null;
    }) {
        this.compositor.selectedElIds = selectedElements;

        if (topLevelSelectedQuantity !== undefined) {
            this.compositor.topLevelSelectedQuantity = topLevelSelectedQuantity;
        }

        if (currentSelectedGroupId !== undefined) {
            this.compositor.currentSelectedGroupId = currentSelectedGroupId;
        }

        if (currentParentId !== undefined) {
            this.compositor.currentParentId = currentParentId;
        }

        if (this.compositor.selectedElIds.length > 0 && !this.compositor.isPlaying) {
            this.transformerBox = new TransformerBox(
                new Position(0, 0),
                new Dimension(0, 0),
                0,
                InteractionStates.SELECTED,
                false,
                this.checkIsElementIsMask,
            );
        } else {
            this.transformerBox = null;
        }
    }

    private onMouseUp = (e) => {
        if (this.compositor.isRotating) {
            this.compositor.isRotating = false;
        }

        if (this.compositor.isDragging) {
            if (this.delayElementSelection) {
                this.requestSelectElement(this.getElementToSelect(isCtrlKey(e)));
                this.delayElementSelection = false;
            }

            this.requestToChangeCursor('pointer');
            this.compositor.isDragging = false;
        }

        if (e && this.compositor.selectedElIds.length && isShiftKey(e) && this.unselectElementId) {
            this.requestDeselectElement(this.unselectElementId);
            this.unselectElementId = null;
        }

        if (this.transformerBox) {
            this.requestInteractElement({
                elementId: this.compositor.selectedElIds[0],
                interactionState: InteractionStates.SELECTED,
            });
        }

        if (this.compositor.isResizing) {
            this.compositor.isResizing = false;
            const calculatedMousePosition: Position = this.compositor.getCalculatedMousePosition();
            this.compositor.resizeHandleIndex = this.transformerBox.isMouseOverHandles(calculatedMousePosition);
            this.redraw(this.compositor.resizeHandleIndex, HandlerInteractionStates.HOVER);
        }
    };

    requestToDrawTransformerBox(handleIndex: CORNERS | SIDES = null, state: HandlerInteractionStates = null) {
        if (this.drawTransformerBoxRequestId) {
            cancelAnimationFrame(this.drawTransformerBoxRequestId);
        }

        this.drawTransformerBoxRequestId = requestAnimationFrame(() => {
            this.drawTransformerBoxRequestId = 0;
            this.drawTransformerBox(handleIndex, state);
        });
    }

    requestToDrawHoverBox() {
        if (this.drawHoverBoxRequestId) {
            cancelAnimationFrame(this.drawHoverBoxRequestId);
        }

        this.drawHoverBoxRequestId = requestAnimationFrame(() => {
            this.drawHoverBoxRequestId = 0;
            this.drawHoverBox();
        });
    }

    drawTransformerBox(handleIndex: CORNERS | SIDES, state: HandlerInteractionStates) {
        if (!this.transformerBox) {
            return;
        }

        if (this.compositor.selectedElIds.length === 1) {
            const [selectedElId] = this.compositor.selectedElIds;
            const selectedEl = this.getElementById(selectedElId);

            if (selectedEl) {
                // todo: Remove after BE implements inherited locks
                const isElementLocked = selectedEl.originalElement.locked;
                // const isElementLocked = isLockedRecursively(selectedEl.originalElement);
                const isResizable =
                    // todo: Remove after BE implements inherited locks
                    !selectedEl.originalElement.locked &&
                    // !isElementLocked &&
                    !(selectedEl.originalElement instanceof GroupElement) &&
                    selectedElId !== this.compositor.textEditorElement;
                const hasDefaultRotation =
                    selectedEl?.originalElement instanceof TextElement &&
                    selectedEl?.originalElement?.isTextEditorActivated;

                this.transformerBox.position = selectedEl.originalElement.position;
                this.transformerBox.dimension = selectedEl.originalElement.dimension;
                this.transformerBox.rotation = hasDefaultRotation ? 0 : selectedEl.originalElement.rotation;
                this.transformerBox.isMultiBox = false;
                this.transformerBox.isContentProperty = !!selectedEl.originalElement.contentPropertyId;
                this.transformerBox.isResizeable = isResizable;

                // Doing it only for text that have checkmark
                if (selectedEl.originalElement instanceof TextElement && selectedEl.originalElement.limitTextToBounds) {
                    const {
                        boundingBox,
                        originalElement: { boundedText },
                    } = selectedEl;
                    this.transformerBox.isInvalid =
                        boundedText.getShapedText().getContentHeight() > boundingBox.dimension.height ||
                        boundedText.getShapedText().getContentWidth() > boundingBox.dimension.width;
                } else {
                    const { isValid, isChildrenValid } = this.validationManager.getElementValidationState(
                        selectedEl.originalElement.id,
                    );
                    this.transformerBox.isInvalid = !isValid || !isChildrenValid;
                }

                this.transformerBox.elementId = this.compositor.selectedElIds[0];
                this.transformerBox.createSelectionHandles(this.compositor.getScale());

                if (!this.transformerBox.isOffsetCalculated) {
                    this.compositor.offset = this.compositor.mousePosition.subtract(this.transformerBox.position);
                }

                this.transformerBox.isOffsetCalculated = true;
                this.boundingBoxCompositor.drawTransformerBox(this.transformerBox, isElementLocked, handleIndex, state);
            }
        } else {
            let x = Number.POSITIVE_INFINITY;
            let y = Number.POSITIVE_INFINITY;
            let width = Number.NEGATIVE_INFINITY;
            let height = Number.NEGATIVE_INFINITY;

            this.transformerBox.isInvalid = this.compositor.selectedElIds.some((elId) => {
                const { isValid, isChildrenValid } = this.validationManager.getElementValidationState(elId);

                return !isValid || !isChildrenValid;
            });

            const selectedEls = this.getElementsByIds(this.compositor.selectedElIds);

            selectedEls.forEach((el) => {
                const boundingBox = getBoundingBoxAfterRotation(el.originalElement);

                x = Math.min(x, ...boundingBox.map((v) => v.x));
                y = Math.min(y, ...boundingBox.map((v) => v.y));
                width = Math.max(width, ...boundingBox.map((v) => v.x));
                height = Math.max(height, ...boundingBox.map((v) => v.y));
            });

            this.transformerBox.position = new Position(x, y);
            this.transformerBox.dimension = new Dimension(width - x, height - y);

            if (!this.transformerBox.isOffsetCalculated) {
                this.compositor.offset = this.compositor.mousePosition.subtract(this.transformerBox.position);
            }

            this.transformerBox.isContentProperty = false;
            this.transformerBox.isOffsetCalculated = true;
            this.transformerBox.isMultiBox = true;
            this.transformerBox.elementId =
                this.compositor.topLevelSelectedQuantity === 1 ? this.compositor.selectedElIds[0] : null;
            this.transformerBox.createMultiElementOwnBoxes(selectedEls);
            this.transformerBox.multiElementOwnBoxes.forEach((subBox) => {
                const { isValid, isChildrenValid } = this.validationManager.getElementValidationState(subBox.elementId);

                subBox.isInvalid = !isValid || !isChildrenValid;
            });
            this.transformerBox.isResizeable = !this.hasLockedAmongSelected();
            this.transformerBox.createSelectionHandles(this.compositor.getScale());

            this.boundingBoxCompositor.drawTransformerBox(this.transformerBox, false, handleIndex, state);
        }

        if (this.compositor.isRotating) {
            const mousePosition = this.compositor.getCalculatedMousePosition();
            this.boundingBoxCompositor.drawRotationLabel(this.transformerBox, mousePosition);
        }
    }

    drawHoverBox() {
        if (!this.hoverBox) {
            return;
        }

        const el = this.getElementById(this.hoverElId);

        if (!el) {
            return;
        }

        this.hoverBox.elementId = Number(el.id);
        this.hoverBox.position = el.originalElement.position;
        this.hoverBox.dimension = el.originalElement.dimension;
        this.hoverBox.rotation = el.originalElement.rotation;
        this.hoverBox.isContentProperty = !!el.originalElement.contentPropertyId;

        if (el.originalElement instanceof TextElement && el.originalElement.limitTextToBounds) {
            const {
                boundingBox,
                originalElement: { boundedText },
            } = el;
            this.hoverBox.isInvalid =
                boundedText.getShapedText().getContentHeight() > boundingBox.dimension.height ||
                boundedText.getShapedText().getContentWidth() > boundingBox.dimension.width;
        } else {
            const { isValid, isChildrenValid } = this.validationManager.getElementValidationState(
                el.originalElement.id,
            );
            this.hoverBox.isInvalid = !isValid || !isChildrenValid;
        }

        this.boundingBoxCompositor.drawHoverBox(this.hoverBox);
    }

    getStartPosition(): Position {
        const selectedEls = this.getElementsByIds(this.compositor.selectedElIds).filter((el, _, arr) => {
            const isParentIncluded = arr.some((selected) => selected.id === el.parent?.id);

            return !isParentIncluded;
        });

        const main = selectedEls[0].originalElement.position.getCopy();

        selectedEls.forEach((el) => {
            const boundingBox = getBoundingBoxAfterRotation(el.originalElement);
            const x = Math.min(...boundingBox.map((v) => v.x));
            const y = Math.min(...boundingBox.map((v) => v.y));

            if (x < main.getX()) {
                main.setX(x);
            }

            if (y < main.getY()) {
                main.setY(y);
            }
        });

        return main;
    }

    private getRotationParams = () => {
        const multipleElements = this.compositor.selectedElIds.length > 1;
        const cX = this.transformerBox.position.x + this.transformerBox.dimension.width / 2;
        const cY = this.transformerBox.position.y + this.transformerBox.dimension.height / 2;
        const rotation = multipleElements
            ? this.transformerBox.virtualRotation
            : this.getElementById(this.compositor.selectedElIds[0]).rotation;
        const calculatedMousePosition: Position = this.compositor.getCalculatedMousePosition();
        const dX = calculatedMousePosition.getX() - cX;
        const dY = calculatedMousePosition.getY() - cY;
        const currentDegree = (Math.atan2(dY, dX) + Math.PI) * (180 / Math.PI);

        return {
            currentDegree,
            rotation,
        };
    };

    private calculateRotateData = () => {
        const { rotation, currentDegree } = this.getRotationParams();

        this.initialDegree = currentDegree;
        this.lastElementDegree = rotation;
    };

    private onDragging(selectedEl: BaseCompElement, position: Position, mouseDragging = true, ignoreSnapping = false) {
        const multipleElements = this.compositor.selectedElIds.length > 1;

        const moveParams: ElementMoveParams = {
            elementsIds: this.compositor.selectedElIds,
            element: selectedEl.originalElement,
            scale: this.compositor.getScale(),
            pixelRatio: this.compositor.devicePixelRatio,
            position,
            multipleElements,
            ignoreSnapping,
        };

        if (multipleElements) {
            this.unselectElementId = null;
            const positionToSub = mouseDragging ? this.getStartPosition() : selectedEl.originalElement.position;
            (moveParams as MultipleElementMoveParams).differencePosition = position.subtract(positionToSub);
        }

        this.emitter.emit<'requestElementMove'>('requestElementMove', moveParams);
    }

    private recalculateSize = (e, mousePosition: Position) => {
        const shouldFire = this.transformerBox.recalculateSize(
            this.compositor.mouseStartPosition,
            mousePosition,
            this.compositor.resizeHandleIndex,
            this.keepAspectRatio,
            isOptionAltKey(e),
        );
        const position = this.transformerBox.position;
        const dimension = this.transformerBox.dimension;

        return { shouldFire, position, dimension };
    };

    moveElementWithKeys = (e: KeyboardEvent) => {
        // todo: Remove after BE implements inherited locks
        const selectedEl = this.getElementById(this.compositor.selectedElIds?.[0]);

        if (!selectedEl || this.compositor.textEditorElement || this.hasLockedAmongSelected()) {
            return;
        }

        // if (!selectedEl || this.compositor.textEditorElement || this.hasLockedAmongSelected()) {
        //     return;
        // }

        if (isArrowKey(e)) {
            const oldPosition = selectedEl.originalElement.position.getCopy();
            const newPosition = BoundingBoxManipulation.movePositionByArrows(e, oldPosition);
            this.onDragging(selectedEl, newPosition, false, true);
        } else if (e.code === 'KeyF') {
            if (this.compositor.selectedElIds.length > 1 || selectedEl.originalElement instanceof GroupElement) {
                return;
            }

            const newDimension = this.compositor.currentCompModel.getDimension();
            const newPosition = new Position(0, 0);
            const startDimension = this.transformerBox.dimension.getCopy();
            const startPosition = this.transformerBox.position.getCopy();

            const { shouldFire } = this.recalculateSize(e, this.compositor.mousePosition);

            if (shouldFire) {
                this.emitter.emit<'requestElementResize'>('requestElementResize', {
                    element: selectedEl.originalElement,
                    elementsIds: this.compositor.selectedElIds,
                    position: newPosition,
                    dimension: newDimension,
                    scale: this.compositor.getScale(),
                    pixelRatio: devicePixelRatio,
                    handleIndex: this.compositor.resizeHandleIndex,
                    mousePosition: this.compositor.mousePosition,
                    ignoreSnapping: false,
                    recalculateSize: (mousePosition) => this.recalculateSize(e, mousePosition),
                    startDimension,
                    startPosition,
                });
            }
        }
    };

    private onMouseMove = (e: MouseEvent) => {
        if (this.compositor.isPlaying || this.compositor.isPanning || SCROLLBARS.includes(this.compositor.scrollbar)) {
            return;
        }

        const selectedEl = this.getElementById(this.compositor.selectedElIds[0]);

        if (selectedEl && !!this.transformerBox) {
            if (this.compositor.isDragging) {
                this.delayElementSelection = false;

                if (!this.transformerBox.isOffsetCalculated) {
                    return;
                }

                this.requestInteractElement({
                    elementId: this.compositor.selectedElIds[0],
                    interactionState: InteractionStates.DRAGGING,
                });

                if (this.compositor.selectedElIds.length > 1) {
                    this.unselectElementId = null;
                }

                const position = this.compositor.mousePosition.subtract(this.compositor.offset);
                this.onDragging(selectedEl, position);
            } else if (this.compositor.isResizing) {
                this.compositor.calculateMousePosition(e, this.compositor.mousePosition);
                this.detectToKeepAspectRatio(isShiftKey(e));
                const startDimension = this.transformerBox.dimension.getCopy();
                const startPosition = this.transformerBox.position.getCopy();

                const { shouldFire } = this.recalculateSize(e, this.compositor.mousePosition);

                if (shouldFire) {
                    this.emitter.emit<'requestElementResize'>('requestElementResize', {
                        element: selectedEl.originalElement,
                        elementsIds: this.compositor.selectedElIds,
                        position: this.transformerBox.position,
                        dimension: this.transformerBox.dimension,
                        scale: this.compositor.getScale(),
                        pixelRatio: devicePixelRatio,
                        handleIndex: this.compositor.resizeHandleIndex,
                        mousePosition: this.compositor.mousePosition,
                        ignoreSnapping: false,
                        recalculateSize: (mousePosition) => this.recalculateSize(e, mousePosition),
                        startDimension,
                        startPosition,
                    });
                }
            } else if (this.compositor.isRotating) {
                const { currentDegree } = this.getRotationParams();
                const [elementId] = this.compositor.selectedElIds;
                const element = this.getElementById(elementId).originalElement;
                let rotation = currentDegree - Math.abs(this.initialDegree);
                rotation = Math.round(rotation + this.lastElementDegree) % 360;

                if (rotation < 0) {
                    rotation = 360 + rotation;
                }

                const differenceRotation = rotation - this.transformerBox.virtualRotation;
                this.transformerBox.virtualRotation = rotation;

                this.emitter.emit<'requestElementRotate'>('requestElementRotate', {
                    elementsIds: this.compositor.selectedElIds,
                    pixelRatio: this.compositor.devicePixelRatio,
                    scale: this.compositor.getScale(),
                    element,
                    rotation,
                    differenceRotation,
                });

                this.changeCursorWhileRotating(this.compositor.resizeHandleIndex);
            }
        }

        if (
            !this.compositor.isResizing &&
            !this.compositor.isDragging &&
            !this.compositor.isRotating &&
            !this.compositor.isPanning &&
            !this.compositor.isPanMode
        ) {
            const elementId = this.getElementToSelect();

            if (elementId && SCROLLBARS.includes(elementId)) {
                return;
            }

            const isHandlerFound = this.getHoveredHandleIndex() !== -1;

            // TODO: Correct these conditions
            if (
                this.hoverElId !== null &&
                ((!!this.hoverElId && !elementId) ||
                    this.compositor.selectedElIds.find((elId) => elId === elementId) ||
                    isHandlerFound)
            ) {
                this.hoverElId = null;
                this.requestHoverElement(null);
            } else if (
                elementId &&
                !isHandlerFound &&
                !this.compositor.selectedElIds.find((elId) => elId === elementId)
            ) {
                this.hoverElId = elementId;
                this.requestHoverElement(elementId);
            }
        }
    };

    private getHoveredHandleIndex() {
        let handleIndex = -1;

        if (this.transformerBox) {
            handleIndex = this.transformerBox.isMouseOverHandles(this.compositor.getCalculatedMousePosition());
        }

        return handleIndex;
    }

    private requestSelectElement(elementId: number, useMultiple = false) {
        if (useMultiple && this.compositor.selectedElIds.length) {
            const firstElement = this.getElementById(this.compositor.selectedElIds[0]);
            const chosenElement = this.getElementById(elementId);

            if (chosenElement?.parent?.id === firstElement?.parent?.id) {
                this.emitter.emit<'requestElementSelect'>('requestElementSelect', { elementId, useMultiple });
            }
        } else {
            this.emitter.emit<'requestElementSelect'>('requestElementSelect', { elementId, useMultiple });
        }
    }

    private requestDeselectElement(elementId: number) {
        const firstElement = this.getElementById(this.compositor.selectedElIds[0]);
        const chosenElement = this.getElementById(elementId);

        if (chosenElement?.parent?.id !== firstElement?.parent?.id && chosenElement?.parent?.id !== firstElement.id) {
            this.emitter.emit<'requestElementDeselect'>('requestElementDeselect', {
                elementId: chosenElement.parent.id,
            });
        } else {
            this.emitter.emit<'requestElementDeselect'>('requestElementDeselect', { elementId });
        }
    }

    private requestInteractElement = ({
        elementId,
        interactionState,
    }: {
        elementId: number;
        interactionState: InteractionStates;
    }) => {
        this.emitter.emit<'requestElementInteract'>('requestElementInteract', { elementId, interactionState });
    };

    private checkIsElementIsMask = (elementId: number): boolean => {
        return this.compositor.currentCompModel?.isElementMask(elementId ?? '') || false;
    };

    hoverElement(elementId: number) {
        if (elementId && !this.compositor.isPlaying) {
            this.hoverBox = new HoverBox(
                new Position(0, 0),
                new Dimension(0, 0),
                0,
                this.checkIsElementIsMask,
                elementId,
            );
        } else {
            this.hoverBox = null;
        }
    }

    requestHoverElement(elementId: number) {
        this.emitter.emit<'requestElementHover'>('requestElementHover', { elementId });
    }

    // todo: Remove after BE implements inherited locks
    private hasLockedAmongSelected = (): boolean => {
        const elements = this.compositor.selectedElIds.map((id) => this.getElementById(id)).filter(Boolean);

        if (elements.length === 0) {
            return false;
        }

        return elements.every((el) => el.originalElement?.locked === true);
    };

    // private hasLockedAmongSelected = () =>
    //     getTopLevelElements(
    //         this.compositor.selectedElIds.map((el) => this.getElementById(el)).filter((el) => !!el),
    //     ).some((element) => 'originalElement' in element && isLockedRecursively(element.originalElement));

    private startDragging() {
        if (this.hasLockedAmongSelected()) {
            return;
        }

        this.compositor.startDragging(this.transformerBox);
        this.requestInteractElement({
            elementId: this.compositor.selectedElIds[0],
            interactionState: InteractionStates.PRESSED,
        });
    }

    private startResizing(handleIndex: number) {
        if (this.hasLockedAmongSelected()) {
            return;
        }

        this.compositor.startResizing(handleIndex);
        this.redraw(handleIndex, HandlerInteractionStates.PRESSED);
    }

    private startRotating() {
        if (this.hasLockedAmongSelected()) {
            return;
        }

        this.compositor.isRotating = true;
        this.compositor.resizeHandleIndex = this.transformerBox.isMouseOverHandles(
            this.compositor.getCalculatedMousePosition(),
        );
        this.calculateRotateData();
    }

    private onMouseDown = (e: MouseEvent) => {
        if (this.compositor.isPlaying) {
            return;
        }

        if (getWhich(e) === 2 || this.compositor.isPanMode) {
            return;
        }

        const selectedElId = this.getElementToSelect(isCtrlKey(e));

        if (selectedElId && SCROLLBARS.includes(selectedElId)) {
            return;
        }

        this.transformerBox?.commit();
        this.compositor.calculateMousePosition(e, this.compositor.mousePosition);
        this.compositor.calculateMousePosition(e, this.compositor.mouseStartPosition);
        const calculatedMousePosition: Position = this.compositor.getCalculatedMousePosition();

        // no selected element
        if (!this.compositor.selectedElIds.length) {
            if (selectedElId) {
                this.pressedElId = selectedElId;
                this.requestSelectElement(selectedElId);
                this.startDragging();
            }
        } else if (!!this.compositor.selectedElIds.length && !!this.transformerBox) {
            // TODO: optimize
            const handleIndex = this.transformerBox.isMouseOverHandles(calculatedMousePosition);
            const selectedEl = this.getElementById(selectedElId);

            if (!selectedEl && handleIndex === -1) {
                this.requestSelectElement(null);

                return;
            }

            if (!this.compositor.textEditorElement) {
                if (isShiftKey(e) && handleIndex === -1) {
                    const isAlreadySelected = this.compositor.selectedElIds.find((elId) => elId === selectedElId);

                    if (!isAlreadySelected) {
                        this.requestSelectElement(selectedElId, true);
                    } else {
                        this.unselectElementId = selectedElId;
                    }

                    return;
                }

                // Here we try to use delaySelection because click was over the transf.box.
                // But! For element that is locked - we pass this logic
                if (
                    this.compositor.selectedElIds.length >= 1 &&
                    handleIndex === -1 &&
                    this.transformerBox.isMouseOverBox(calculatedMousePosition) &&
                    // todo: Remove after BE implements inherited locks
                    !selectedEl?.originalElement?.locked
                    // !this.hasLockedAmongSelected()
                ) {
                    this.delayElementSelection = true;
                    this.startDragging();

                    return;
                }
            }

            // NOTE: figure out this
            if (this.compositor.selectedElIds.includes(this.hoverElId)) {
                this.hoverElId = null;
                this.requestHoverElement(null);
            }

            if (handleIndex > -1) {
                if (ROTATIONS[handleIndex]) {
                    this.startRotating();
                } else {
                    this.startResizing(handleIndex);
                    this.detectToKeepAspectRatio(isShiftKey(e));
                }
            } else if (
                this.compositor.selectedElIds.length === 1 &&
                !this.hoverElId &&
                !this.compositor.textEditorElement &&
                selectedEl &&
                this.transformerBox.isMouseOverBox(calculatedMousePosition) &&
                // todo: Remove after BE implements inherited locks
                !selectedEl.originalElement.locked
                // !isLockedRecursively(selectedEl?.originalElement)
            ) {
                this.startDragging();
            } else if (!this.compositor.textEditorElement) {
                // here can be null
                this.requestSelectElement(selectedElId);

                if (selectedElId) {
                    this.startDragging();
                    this.requestInteractElement({
                        elementId: selectedElId,
                        interactionState: InteractionStates.PRESSED,
                    });
                }
            }
        } else {
            this.requestSelectElement(null);
        }
    };

    changeCursorWhileRotating(handleIndex: ROTATIONS) {
        const isMultiSelection = this.compositor.selectedElIds.length > 1;
        const [elementId] = this.compositor.selectedElIds;
        const rotation = isMultiSelection
            ? this.transformerBox.virtualRotation
            : this.getElementById(elementId).originalElement.rotation;

        switch (handleIndex) {
            case ROTATIONS.LEFT_TOP:
                this.canvas.style.cursor = getRotatedCursor(180 + rotation);
                break;
            case ROTATIONS.RIGHT_TOP:
                this.canvas.style.cursor = getRotatedCursor(270 + rotation);
                break;
            case ROTATIONS.LEFT_BOTTOM:
                this.canvas.style.cursor = getRotatedCursor(90 + rotation);
                break;
            case ROTATIONS.RIGHT_BOTTOM:
                this.canvas.style.cursor = getRotatedCursor(rotation);
                break;
        }
    }

    private detectToKeepAspectRatio(isShiftKeyPressed: boolean) {
        if (this.compositor.selectedElIds.length > 1) {
            this.keepAspectRatio = true;
        } else {
            const selectedEl = this.getElementById(this.compositor.selectedElIds[0]);

            if (!selectedEl) {
                return;
            }

            const keepAspectRatio = selectedEl.originalElement.lockUniScaling || false;
            this.keepAspectRatio = keepAspectRatio
                ? keepAspectRatio
                : isShiftKeyPressed
                ? !keepAspectRatio
                : keepAspectRatio;
        }
    }

    unsubscribe() {
        this.containerDocument.removeEventListener('mousedown', this.onMouseDown);
        this.containerDocument.removeEventListener('mouseup', this.onMouseUp);
        this.containerDocument.removeEventListener('mousemove', this.onMouseMove);
    }
}

export default BoundingBoxManipulation;
